import * as actionCreators from './actions';

/**
 * Какие экшены, пришедшие по вебсокету матча, разрешено применять.
 *
 * Стор у клиента (и у лобби на сервере) один на всё: игра, профиль, инвентарь,
 * модалки, туториал. Редьюсеры SET_PLAYER_AUTH, ADD_OWNED_UNIT_SKIN,
 * RESET_STATE — такие же «известные типы», как ход в игре, и применили бы
 * что угодно, пришедшее от соседа по лобби. Поэтому от игрока принимается
 * только то, что честный клиент вообще отправляет на сервер.
 *
 * Список руками не ведется — он выводится из src/redux/actions.js по тому же
 * признаку, по которому networkMiddleware решает, отправлять ли экшен:
 * создатель без noServerSync — игровое действие матча, его можно
 * ретранслировать; с noServerSync — локальный экшен клиента, от чужих не
 * принимается. Новый игровой экшен попадает в список сам.
 *
 * Экшены, которые рассылает САМ сервер (таймер хода, серверное исполнение
 * ходов ботов и т.п., часто с noServerSync), идут с серверной меткой —
 * ее ставит broadcastToLobby, и клиент принимает их без сверки типа. Метку,
 * пришедшую от клиента, сервер в RELAY_ACTION срезает — подделать ее нельзя.
 */

export const SERVER_ORIGIN_MARK = '_serverOrigin';

// Тип и noServerSync создатель выставляет сам, от аргументов они не зависят —
// поэтому достаточно вызвать его «вхолостую». Часть создателей
// деструктурирует аргументы — для них вторая попытка с пустыми объектами.
// Создатель, который так и не удалось вызвать, в список не попадает (закрыто
// по умолчанию); если это игровой экшен, упадет relayedActions.test.js
const PROBE_ARGS_VARIANTS = [[], [{}, {}, {}, {}, {}, {}]];

export const probeActionCreator = (creator) => {
  for (const args of PROBE_ARGS_VARIANTS) {
    try {
      const action = creator(...args);
      if (action && typeof action.type === 'string') {
        return action;
      }
    } catch (e) {
      // следующая попытка
    }
  }
  return null;
};

const collectRelayableActionTypes = () => {
  const types = new Set();
  Object.values(actionCreators).forEach((creator) => {
    if (typeof creator !== 'function') {
      return;
    }
    const action = probeActionCreator(creator);
    if (action && !action.noServerSync) {
      types.add(action.type);
    }
  });
  return types;
};

export const RELAYABLE_ACTION_TYPES = collectRelayableActionTypes();

// Пустой список означал бы, что создатели экшенов не прочитались (например,
// сломался импорт) — сервер молча отбрасывал бы все ходы. Лучше упасть сразу
if (RELAYABLE_ACTION_TYPES.size === 0) {
  throw new Error('[relayedActions] не удалось вывести ни одного игрового экшена из redux/actions');
}

// Вне production сервер ретранслирует и LOAD_STATE: так e2e-скрипты
// (combatStageLeftParticipant, combatStageSimultaneousConfirm) переносят
// подготовленное состояние боя в живое лобби. На проде это подмена всего
// состояния матча у всех участников — там закрыто. Честный клиент LOAD_STATE
// не шлет никогда (BLACKLISTED_TYPES в networkMiddleware)
const DEV_ONLY_RELAYABLE_ACTION_TYPES = new Set(['LOAD_STATE']);

// Имеет ли игрок право ретранслировать экшен этого типа (проверка сервера).
// По умолчанию — как на проде: исключения для разработки выключены
export const checkIfActionTypeRelayable = (type, {isDevEnvironment = false} = {}) => (
  RELAYABLE_ACTION_TYPES.has(type) || (isDevEnvironment && DEV_ONLY_RELAYABLE_ACTION_TYPES.has(type))
);

// Серверная метка для экшенов, которые сервер рассылает от себя
export const markServerOriginAction = (action) => ({...action, [SERVER_ORIGIN_MARK]: true});

// Можно ли применить пришедший по сокету экшен (проверка клиента)
export const checkIfIncomingActionAllowed = (msg) => Boolean(msg)
  && (msg[SERVER_ORIGIN_MARK] === true || checkIfActionTypeRelayable(msg.type));
